import type { FormQuestion, FormResponse } from "../../types/form";
import type {
  InvestorAlphaPreference,
  InvestorAlphaRecommendationId,
} from "../../types/investorAlpha";

export type AlphaInvestmentTrackUpdateResult = "applied" | "superseded";

export type AlphaInvestmentTrackUpdateHandler = (
  preference: InvestorAlphaPreference,
  recalculateRiskScore?: boolean
) => AlphaInvestmentTrackUpdateResult
  | void
  | Promise<AlphaInvestmentTrackUpdateResult | void>;

export const ALPHA_INVESTMENT_TRACK_ANSWERS: Record<
  InvestorAlphaPreference,
  string
> = {
  growth: "total returns growth",
  balance: "total returns balanced",
  protect: "income",
};

export const ALPHA_RECOMMENDATION_PREFERENCES: Record<
  InvestorAlphaRecommendationId,
  InvestorAlphaPreference
> = {
  growth: "growth",
  balanced: "balance",
  protect: "protect",
};

export const ALPHA_PREFERENCE_RECOMMENDATIONS: Record<
  InvestorAlphaPreference,
  InvestorAlphaRecommendationId
> = {
  growth: "growth",
  balance: "balanced",
  protect: "protect",
};

const normalizeQuestionField = (value: unknown) =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

export function getInvestmentTrackQuestion(
  questions: FormQuestion[]
): FormQuestion {
  const matchingQuestions = questions.filter(
    (question) =>
      normalizeQuestionField(question.question) === "investment track"
      && normalizeQuestionField(question.category) === "kyc"
      && normalizeQuestionField(question.subcategory) === "kyc"
  );

  if (matchingQuestions.length !== 1) {
    throw new Error("Investment track question contract is invalid");
  }

  const question = matchingQuestions[0];
  if (!Number.isInteger(question.id) || question.id <= 0) {
    throw new Error("Investment track question ID is invalid");
  }
  if (normalizeQuestionField(question.type) !== "select") {
    throw new Error("Investment track question must be a select");
  }

  let options: unknown;
  try {
    options = JSON.parse(question.options);
  } catch {
    throw new Error("Investment track options are invalid");
  }

  const expectedOptions = Object.values(ALPHA_INVESTMENT_TRACK_ANSWERS);
  if (
    !Array.isArray(options)
    || options.length !== expectedOptions.length
    || options.some((option) => typeof option !== "string")
    || expectedOptions.some((option) => !options.includes(option))
  ) {
    throw new Error("Investment track options do not match the supported choices");
  }

  return question;
}

export function getSavedInvestmentTrackPreference(
  question: FormQuestion,
  responses: FormResponse[]
): InvestorAlphaPreference | null {
  if (responses.length === 0) return null;
  if (responses.length !== 1 || responses[0].questionId !== question.id) {
    throw new Error("Saved Investment track response contract is invalid");
  }

  const savedEntry = Object.entries(ALPHA_INVESTMENT_TRACK_ANSWERS).find(
    ([, answer]) => answer === responses[0].answer
  );
  if (!savedEntry) {
    throw new Error("Saved Investment track answer is not supported");
  }

  return savedEntry[0] as InvestorAlphaPreference;
}

export function getRecommendationInvestmentTrackAnswer(
  recommendationId: InvestorAlphaRecommendationId
): string {
  return ALPHA_INVESTMENT_TRACK_ANSWERS[
    ALPHA_RECOMMENDATION_PREFERENCES[recommendationId]
  ];
}
